import React, { useContext, useEffect, useState } from 'react';
import { Typography, Link } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LicensesSection, { MedicalLicense } from './LicensesSection';
import PARSConsentSection from './PARSConsentSection';
import { userContext } from '../context/userContext';
import { contactContext } from '../context/contactContext';
import { isMember } from '../utils/membershipUtils';
import { useLocation } from 'react-router-dom';
import { CMEAccountURL, CMECredentialsURL, DefaultNoImageURL, HeaderEclipsePhotoURL, FEATURE_ACCREDITATION_ENABLED } from '../config/config';
import '../styles/learner-profile.css';

const formatBirthdate = (birthdate: string | null | undefined): string => {
  if (!birthdate) return '';
  const parts = birthdate.split('T')[0].split('-');
  if (parts.length < 3) return '';
  return `${parts[1].padStart(2, '0')}/${parts[2].padStart(2, '0')}`;
};

// Headshot outcomes by url, kept across mounts. Each dashboard page (Claim, Transcript, …) mounts
// its own header, so component state alone resets on every page switch. A real headshot comes
// back from the browser cache instantly, but a missing one is a 404 the browser re-requests, which
// left the photo blank until the error arrived and then swapped in the stand-in — a visible flash.
const photoOutcomeByUrl = new Map<string, boolean>();

interface LearnerProfileHeaderProps {
  onLicensesLoaded?: (licenses: MedicalLicense[]) => void;
}

const LearnerProfileHeader: React.FC<LearnerProfileHeaderProps> = ({ onLicensesLoaded }) => {
  const { User: userData } = useContext(userContext);
  const { contactObject, isLoading: isContactLoading } = useContext(contactContext);
  const contactData = contactObject.Contact;
  const location = useLocation();

  // Outcome of the headshot request, tagged with the url it belongs to. Keying on the url (not
  // a bare boolean) means switching to another learner drops the previous result immediately
  // instead of carrying it over. Until this settles nothing is shown at all — the stand-in is
  // only correct once we know the learner really has no headshot, not while we are still asking.
  const [photoResult, setPhotoResult] = useState<{ src: string; ok: boolean } | null>(null);

  const isAdmin: boolean = userData.acepMemberStatus === 2;
  const isMemberUser: boolean = isMember({ acepMemberStatus: userData.acepMemberStatus });
  const isNonPhysician: boolean = contactData?.IsNonPhysician === true;
  const path = location.pathname.toLowerCase();

  const isNonMemberUser = !!(userData.acepid &&
    ((userData.acepMemberStatus === 0 && userData.sempaMemberStatus !== 15) ||
      userData.acepMemberStatus === 3 ||
      userData.acepMemberStatus === 4 ||
      userData.acepMemberStatus === 7));

  // When admin proxies a member, derive visibility rules from the proxied member's data (contactData),
  // so staff sees an identical header to what the member themselves would see.
  const proxiedIsNonMember = isAdmin && !!(contactData?.acepid &&
    ((contactData.acepMemberStatus === 0 && contactData.sempaMemberStatus !== 15) ||
      contactData.acepMemberStatus === 3 ||
      contactData.acepMemberStatus === 4 ||
      contactData.acepMemberStatus === 7));
  const proxiedIsMember = isAdmin && isMember({ acepMemberStatus: contactData?.acepMemberStatus });

  const isHomePage =
    path === '/' || path === '/dashboard' || path === '/index.html' || path === '/index.htm';

  const isClaimOrTranscriptPage =
    path === '/dashboard/unclaimedcme' || path === '/dashboard/mostrecentactivities';

  // Pages where admin sees no header at all
  const isAdminNoHeaderPage =
    isHomePage || path === '/dashboard/mergecme' || path === '/dashboard/managerequirements';

  const hasMDorDO = (val: string | undefined) => (val || '').toUpperCase().split(/[\s,]+/).some(d => d === 'MD' || d === 'DO');
  const isMDorDO = hasMDorDO(contactData?.professionaldesignation) || hasMDorDO(contactData?.jobtitle);

  // Whether the header should render at all:
  //   Admin on own pages                → hidden (Home, MergeCME, ManageRequirements)
  //   Admin proxying anyone             → basic contact info header on all other pages
  //   Member (MD/DO)                    → Home + Claim/Transcript pages
  //   Member (non-MD/DO)                → Home only
  //   Non-member                        → Home only
  const showHeader = isAdmin
    ? !isAdminNoHeaderPage
    : isNonMemberUser
      ? isHomePage
      : isHomePage || (isClaimOrTranscriptPage && isMDorDO && isMemberUser);

  // Admin proxying: the headshot is keyed on the learner's acepid, taken from ?acepid= so it is
  // known on mount — before the contact lookup returns. Members keep their own avatar from the
  // profile API. Empty means there is no headshot to ask for at all, so the stand-in applies.
  const proxiedAcepId = new URLSearchParams(location.search).get('acepid') || contactData?.acepid;
  const photoSrc = isAdmin
    // S3 keys are case-sensitive and headshots are stored under the upper-case id (A403398), but
    // a hand-typed ?acepid= is often lower-case (a403398), which S3 answers with 403.
    ? (proxiedAcepId ? `${HeaderEclipsePhotoURL}${proxiedAcepId.toUpperCase()}/avatar.png` : '')
    : ((userData as any)?.avatar || '');

  // Start the headshot request as soon as the url is known, while the page is still loading and
  // the header is still hidden, so the photo is ready when the header appears.
  useEffect(() => {
    if (!photoSrc) return;
    const known = photoOutcomeByUrl.get(photoSrc);
    if (known !== undefined) {
      setPhotoResult({ src: photoSrc, ok: known });
      return;
    }
    let active = true;
    const img = new Image();
    img.onload = () => { photoOutcomeByUrl.set(photoSrc, true); if (active) setPhotoResult({ src: photoSrc, ok: true }); };
    img.onerror = () => { photoOutcomeByUrl.set(photoSrc, false); if (active) setPhotoResult({ src: photoSrc, ok: false }); };
    img.src = photoSrc;
    return () => { active = false; };
  }, [photoSrc]);

  if (!showHeader) return null;

  // Nothing is rendered until the learner this page is for has actually loaded. contactContext
  // clears the contact whenever the target acepid changes, so a non-empty contactid here always
  // belongs to the person in the URL — never the previously viewed learner or the signed-in
  // admin. Without this the header painted names, a location and a stand-in headshot from
  // whatever happened to be in context.
  if (isContactLoading || !contactData?.contactid) return null;

  // Licenses + PARS consent columns:
  //   Admin proxying MD/DO member       → Claim/Transcript only
  //   Admin proxying non-MD/DO or non-member → hidden (basic header only)
  //   Member (MD/DO, not non-physician) → Claim/Transcript only
  const showLicensureConsent = isClaimOrTranscriptPage &&
    !isNonPhysician && isMDorDO &&
    (isAdmin ? proxiedIsMember : (!isNonMemberUser && isMemberUser));

  // NPI and Birth Date only shown for MD/DO members (or admin proxying an MD/DO member)
  const showNPIAndBirthDate = !isNonPhysician && isMDorDO &&
    (isAdmin ? proxiedIsMember : isMemberUser);

  // Settled once the browser has told us whether THIS url resolved. A 404 (learner has no
  // headshot on file) falls back to the shared stand-in, exactly as before — the change is only
  // that the stand-in no longer appears while the request is still outstanding.
  // An outcome already known from an earlier page settles it straight away, with no blank gap.
  const knownPhotoOutcome = photoOutcomeByUrl.get(photoSrc);
  const currentPhotoOutcome = photoResult !== null && photoResult.src === photoSrc ? photoResult.ok : undefined;
  const photoOk = knownPhotoOutcome ?? currentPhotoOutcome;
  const photoSettled = photoOk !== undefined;
  const useFallbackPhoto = !photoSrc || photoOk === false;
  const recordPhotoOutcome = (ok: boolean) => {
    photoOutcomeByUrl.set(photoSrc, ok);
    setPhotoResult({ src: photoSrc, ok });
  };

  const nameParts = [contactData?.firstname, contactData?.middlename, contactData?.lastname].filter(Boolean).join(' ');
  const titleParts = [contactData?.jobtitle, contactData?.professionaldesignation].filter(Boolean).join(', ');
  const fullName = titleParts ? `${nameParts}, ${titleParts}` : nameParts;

  // Learner's address only. userData is always the signed-in account, so falling back to it
  // showed the admin's own city/state/country underneath the proxied learner's name.
  const locationParts = [
    contactData?.city,
    contactData?.state,
    contactData?.country,
  ].filter(Boolean).join(', ');

  return (
    <div className={`learner-profile-header${showLicensureConsent && !FEATURE_ACCREDITATION_ENABLED ? ' learner-profile-header--no-pars' : ''}`}>

      {/* Left column — always visible */}
      <div className="learner-profile-identity-col">
        {useFallbackPhoto ? (
          <img src={DefaultNoImageURL} alt="" className="learner-profile-photo" />
        ) : (
          <img
            src={photoSrc}
            alt=""
            onLoad={() => recordPhotoOutcome(true)}
            onError={() => recordPhotoOutcome(false)}
            className="learner-profile-photo"
            style={{ visibility: photoSettled ? 'visible' : 'hidden' }}
          />
        )}
        <div>
          <div className="learner-profile-name-row">
            <Typography className="learner-profile-name">
              {fullName}
            </Typography>

            {showLicensureConsent && (
              <Link href={CMEAccountURL} target="_blank" rel="noopener noreferrer" className="learner-profile-edit-link">
                <EditOutlinedIcon className="licenses-section-edit-icon" />
              </Link>
            )}

          </div>
          {locationParts && (
            <Typography variant="body2" className="learner-profile-location">
              <LocationOnOutlinedIcon style={{ fontSize: '16px', flexShrink: 0 }} />
              {locationParts}
            </Typography>
          )}
          {showNPIAndBirthDate && (
            <Typography variant="body2" className="learner-profile-meta-text">
              NPI: {contactData?.NPI_Number ?? ''}
            </Typography>
          )}
          {showNPIAndBirthDate && contactData?.Birthdate && (
            <Typography variant="body2" className="learner-profile-meta-text">
              Birth Date: {formatBirthdate(contactData.Birthdate)}
            </Typography>
          )}
        </div>
      </div>

      {/* Licensure + Consent columns */}
      {showLicensureConsent && (
        <>
          <div className={`learner-profile-licenses-col${!FEATURE_ACCREDITATION_ENABLED ? ' learner-profile-licenses-col--full' : ''}`}>
            <LicensesSection
              onLicensesLoaded={onLicensesLoaded || (() => {})}
              credentialsUrl={CMECredentialsURL}
            />
          </div>
          {FEATURE_ACCREDITATION_ENABLED && (
            <div className="learner-profile-pars-col">
              <PARSConsentSection />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LearnerProfileHeader;
